import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Client, DeliveryRow, DeliverySearch } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { DeliveryActions, DeliveryStatusPill, route } from '../components/DeliveryActions';
import { useDirectory, useNames } from '../lib/directory';
import { useLive } from '../lib/realtime';


const FILTERS = ['q', 'from', 'to', 'driverId', 'status'] as const;
const STATUS_CHOICES: [NonNullable<DeliverySearch['status']>, string][] = [['open', '進行中（受付待ち〜配達中）'], ['unassigned', '未割り当て'], ['done', '完了'], ['canceled', '取り消し・付け替え']];

/** 配送の検索（仕様書「配送の検索・写真、記録」）：配送番号・日付の範囲・ドライバー・状態。条件は URL に残す */
export function DeliveriesPage() {
  const [rows, setRows] = useState<DeliveryRow[] | null>(null);
  const [err, setErr] = useState('');
  const [sp, setSp] = useSearchParams();
  const names = useNames();
  const dir = useDirectory();
  const q = sp.get('q') ?? '';
  const qs = new URLSearchParams(FILTERS.flatMap((k) => (sp.get(k) ? [[k, sp.get(k)!]] : []))).toString();
  const load = () => api.get<{ deliveries: DeliveryRow[] }>(`/v1/office/deliveries${qs ? `?${qs}` : ''}`)
    .then((r) => { setRows(r.deliveries); setErr(''); })
    .catch((e) => { setRows([]); setErr(e instanceof HttpError ? e.message : '読み込めませんでした'); });
  const [clients, setClients] = useState<Client[]>([]);
  useEffect(() => { api.get<{ clients: Client[] }>('/v1/office/clients').then((r) => setClients(r.clients)).catch(() => {}); }, []);
  useEffect(() => { setRows(null); void load(); }, [qs]);
  useLive(['deliveries'], load);
  const set = (k: (typeof FILTERS)[number], v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const filtered = FILTERS.some((k) => sp.get(k));
  const drivers = [...dir.drivers.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name, 'ja'));
  const list = rows ?? [];
  return (
    <div className="page" style={{ display: 'grid', gap: 12 }}>
      <form className="panel" onSubmit={(e) => e.preventDefault()} style={{ padding: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'end' }} aria-label="配送の検索">
        <span className="field"><label htmlFor="ds-q">配送番号</label><input id="ds-q" value={q} onChange={(e) => set('q', e.target.value.trim().toUpperCase())} style={{ width: 110 }} maxLength={6} /></span>
        <span className="field"><label htmlFor="ds-from">作った日（から）</label><input id="ds-from" type="date" value={sp.get('from') ?? ''} onChange={(e) => set('from', e.target.value)} style={{ width: 160 }} /></span>
        <span className="field"><label htmlFor="ds-to">（まで）</label><input id="ds-to" type="date" value={sp.get('to') ?? ''} onChange={(e) => set('to', e.target.value)} style={{ width: 160 }} /></span>
        <span className="field"><label htmlFor="ds-driver">ドライバー</label><select id="ds-driver" value={sp.get('driverId') ?? ''} onChange={(e) => set('driverId', e.target.value)}>
          <option value="">すべて</option>{drivers.map(([id, d]) => <option key={id} value={id}>{d.name}</option>)}</select></span>
        <span className="field"><label htmlFor="ds-status">状態</label><select id="ds-status" value={sp.get('status') ?? ''} onChange={(e) => set('status', e.target.value)}>
          <option value="">すべて</option>{STATUS_CHOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></span>
        {filtered && <button type="button" className="btn" onClick={() => setSp(new URLSearchParams(), { replace: true })}>条件を消す</button>}
      </form>
      {err && <p className="err" role="alert">{err}</p>}
      <div className="panel">
        <table>
          <thead><tr><th>配送番号</th><th>積む → 届ける</th><th>荷主</th><th>必着</th><th>状態</th><th></th></tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={6} className="muted">読み込み中…</td></tr>
              : list.length === 0 ? <tr><td colSpan={6} className="muted">{filtered ? '見つかりませんでした' : 'まだ配送がありません。「配送指示」から作れます'}</td></tr>
              : list.map((r) => (
                <tr key={r.deliveryNo}>
                  <td className="num"><b>{r.deliveryNo}</b></td><td>{route(r, names)}</td><td>{clients.find((c) => c.id === r.clientId)?.name ?? '—'}</td><td className="num">{r.dueAt ?? '—'}</td>
                  <td><DeliveryStatusPill status={r.status} /></td>
                  <td><DeliveryActions row={r} onChanged={load} /></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
